import axios from 'axios';

import { env } from '@/config/env';
import type { ApiResponse } from '@/entities/api/types';
import type { LoginResponse } from '@/features/auth/types/auth';
import { normalizeApiPayload } from '@/utils/normalizeApi';

const authClient = axios.create({
  baseURL: env.VITE_API_BASE_URL,
  timeout: 10000
});

let refreshPromise: Promise<LoginResponse> | null = null;

export function shouldClearSessionOnRefreshFailure(error: unknown) {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    return status === 400 || status === 401 || status === 403;
  }

  if (error instanceof Error) {
    return (
      error.message === 'Missing refresh token.' ||
      error.message === 'Invalid refresh response from server.'
    );
  }

  return false;
}

export async function refreshAuthSession(refreshToken: string) {
  if (!refreshToken) {
    throw new Error('Missing refresh token.');
  }

  refreshPromise ??= authClient
    .post<ApiResponse<LoginResponse>>('/auth/refresh', { refreshToken })
    .then(({ data }) => {
      const result = normalizeApiPayload(data).data;

      if (!result) {
        throw new Error('Invalid refresh response from server.');
      }

      return result;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}
